import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AnalyticsView } from "@/components/admin/AnalyticsView";
import { AlertsView } from "@/components/admin/AlertsView";
import { PipelineView } from "@/components/admin/PipelineView";
import { ProjectsView } from "@/components/admin/ProjectsView";
import { ConversationsView } from "@/components/admin/ConversationsView";
import { ContactsView } from "@/components/admin/ContactsView";
import { PassesView } from "@/components/admin/PassesView";
import { ConfigView } from "@/components/admin/ConfigView";
import { AuditView } from "@/components/admin/AuditView";
import { AutomationView } from "@/components/admin/AutomationView";
import { OptimizerView } from "@/components/admin/OptimizerView";
import { VisitorActivity } from "@/components/admin/VisitorActivity";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "analytics", label: "Analytics" },
  { id: "pipeline", label: "Pipeline" },
  { id: "alerts", label: "Alerts" },
  { id: "automation", label: "Automation" },
  { id: "optimizer", label: "Optimizer" },
  { id: "audit", label: "Audit log" },
  { id: "projects", label: "Projects" },
  { id: "conversations", label: "Conversations" },
  { id: "contacts", label: "Contacts" },
  { id: "passes", label: "Access codes" },
  { id: "config", label: "Agent settings" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("analytics");
  const [data, setData] = useState({});
  const [token, setToken] = useState(() => localStorage.getItem("vale_admin_token"));
  const [visitorSid, setVisitorSid] = useState(() => new URLSearchParams(window.location.search).get("visitor"));

  const closeVisitor = () => {
    setVisitorSid(null);
    window.history.replaceState({}, "", "/admin");
  };

  const logout = useCallback(() => {
    localStorage.removeItem("vale_admin_token");
    setToken(null);
    setData({});
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    const responses = await Promise.all([
      fetch(`${API}/admin/analytics`, { headers: h }),
      fetch(`${API}/admin/conversations`, { headers: h }),
      fetch(`${API}/admin/contacts`, { headers: h }),
      fetch(`${API}/admin/config`, { headers: h }),
      fetch(`${API}/admin/passes`, { headers: h }),
      fetch(`${API}/admin/alerts`, { headers: h }),
      fetch(`${API}/admin/projects`, { headers: h }),
      fetch(`${API}/admin/enquiries`, { headers: h }),
      fetch(`${API}/admin/audit`, { headers: h }),
      fetch(`${API}/admin/webhooks`, { headers: h }),
      fetch(`${API}/admin/settings`, { headers: h }),
      fetch(`${API}/admin/optimizer`, { headers: h }),
    ]);
    if (responses.some((r) => r.status === 401)) { logout(); return; }
    const [analytics, convos, contacts, config, passes, alerts, projects, enquiries, audit, webhooks, settings, optimizer] = await Promise.all(responses.map((r) => r.json()));
    setData({ analytics, convos, contacts, config, passes, alerts, projects, enquiries, audit, webhooks, settings, optimizer });
  }, [token, logout]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <AdminLogin api={API} onLogin={setToken} />;

  const authHeaders = { Authorization: `Bearer ${token}` };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col md:flex-row" data-testid="admin-dashboard">
      <aside className="md:w-64 border-b md:border-b-0 md:border-r border-[#2E2E2E] md:min-h-screen p-5 flex md:flex-col gap-2 overflow-x-auto shrink-0">
        <Link to="/" className="hidden md:flex items-center gap-2 text-[15px] font-semibold text-[#D6D6D6] hover:text-white mb-6 transition-colors duration-150" data-testid="back-to-agent">
          Back to the site
        </Link>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-3 text-[15px] font-semibold px-4 py-3 rounded-xl text-left whitespace-nowrap transition-colors duration-150 ${tab === t.id ? "bg-white text-black" : "text-[#D6D6D6] hover:bg-[#1A1A1A]"}`}>
            {t.label}
            {t.id === "alerts" && (data.alerts?.unread || 0) > 0 && (
              <span data-testid="alerts-unread-badge"
                className={`ml-auto text-[14px] font-bold rounded-full px-2 py-0.5 ${tab === t.id ? "bg-black text-[#FF5C5C]" : "bg-[#DC2626] text-white"}`}>
                {data.alerts.unread}
              </span>
            )}
          </button>
        ))}
        <button onClick={logout} data-testid="admin-logout-button"
          className="flex items-center gap-3 text-[15px] font-semibold px-4 py-3 rounded-xl text-[#D6D6D6] hover:text-[#FF5C5C] hover:bg-[#1A1A1A] text-left whitespace-nowrap transition-colors duration-150 md:mt-auto">
          Sign out
        </button>
      </aside>
      <main className="flex-1 p-5 sm:p-8 overflow-y-auto">
        <h1 className="font-display text-3xl mb-8">{TABS.find((t) => t.id === tab)?.label}</h1>
        {tab === "analytics" && <AnalyticsView data={data.analytics} />}
        {tab === "pipeline" && <PipelineView data={data.enquiries} api={API} authHeaders={authHeaders} onChanged={load} />}
        {tab === "alerts" && <AlertsView data={data.alerts} api={API} authHeaders={authHeaders} onRead={load} />}
        {tab === "automation" && <AutomationView webhooks={data.webhooks} settings={data.settings} api={API} authHeaders={authHeaders} onChanged={load} />}
        {tab === "optimizer" && <OptimizerView data={data.optimizer} api={API} authHeaders={authHeaders} onChanged={load} />}
        {tab === "audit" && <AuditView data={data.audit} />}
        {tab === "projects" && <ProjectsView data={data.projects} api={API} authHeaders={authHeaders} onSaved={load} />}
        {tab === "conversations" && <ConversationsView conversations={data.convos?.conversations} api={API} authHeaders={authHeaders} />}
        {tab === "contacts" && <ContactsView data={data.contacts} />}
        {tab === "passes" && <PassesView data={data.passes?.passes} api={API} authHeaders={authHeaders} onSaved={load} />}
        {tab === "config" && <ConfigView config={data.config} api={API} onSaved={load} authHeaders={authHeaders} />}
      </main>
      {visitorSid && <VisitorActivity sid={visitorSid} api={API} authHeaders={authHeaders} onClose={closeVisitor} />}
    </div>
  );
}
